import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from '@services/auth/auth.service';
import { countries } from '@airwallet/shared_data/countries';
import { environment } from '../../../../environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HelperService } from '../../../services/helper/helper.service';
import { CreateNewCustomer } from '@airwallet/shared-models/details';
import { BusinessType, StripeRegions } from '@airwallet/shared-models/stripe';
import { Router } from '@angular/router';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { Title } from '@angular/platform-browser';
import { NgIf } from '@angular/common';
import { AwCheckboxComponent } from '@components/misc/aw-checkbox/aw-checkbox.component';
import { AuthPageService } from '@services/auth/auth-page.service';
import { euCountries } from '@airwallet/shared_data/euCountries';
import { CountryService } from '@services/country/country.service';
import { ToastService } from '@services/helper/toast.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, NgIf, TranslateModule, AwCheckboxComponent, NgSelectComponent, MatRadioGroup, MatRadioButton]
})
export class SignUpComponent implements OnInit, OnDestroy {
    signUpForm: UntypedFormGroup;
    formSubmitted = false;
    createAccountBtnLabel = 'Create an account';
    supportPhone: { us: { phoneNumber: string; telLink: string }; main: { phoneNumber: string; telLink: string } } = environment.supportPhone;
    pickedRegion: StripeRegions = StripeRegions.EU;
    sign_up: string;
    gdprPolicy: string = environment.gdprPolicy;
    euTermsOfUse: string = environment.euTermsOfUse;
    usTermsOfUse: string = environment.usTermsOfUse;
    gdprChecked: boolean;
    termsChecked: boolean;
    termsOfUseAndGDPRDisabled = true;
    isDemo = environment.isDemo;
    constructor(
        private authService: AuthService,
        protected helperService: HelperService, // used in HTML
        private toastService: ToastService,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private translate: TranslateService,
        private title: Title,
        protected authPageService: AuthPageService,
        protected countryService: CountryService
    ) {}

    ngOnInit() {
        this.title.setTitle('Sign up');

        const user: DashboardOwnerUser = this.helperService.getDashboardOwnerUser(true);
        if (user) this.router.navigate([`/home`]);

        this.sign_up = this.translate.instant('sign_up.sign_up');

        this.setupSignUpForm();
        this.onChanges();
    }

    ngOnDestroy() {
        if (!environment.isDemo) {
            this.authPageService.loading.set(false);
        }
        this.authPageService.isCompanyIndividualSignUp.set(false);
    }

    onChanges(): void {
        this.signUpForm.valueChanges.subscribe(val => {
            const repeatPasswordValue: string = val.repeatPassword;
            const passValue: string = val.password;

            if (val.country_code) {
                for (const code in countries) {
                    if (code === val.country_code) {
                        this.termsOfUseAndGDPRDisabled = false; // enable terms and gdpr, cannot unselect country pick again so will be enabled until page reload

                        if (this.pickedRegion !== countries[code].platform) {
                            // region changed, uncheck terms and gdpr
                            this.termsChecked = false;
                            this.gdprChecked = false;
                        }

                        this.pickedRegion = countries[code].platform;
                        this.authPageService.isCompanyIndividualSignUp.set(euCountries.includes(val.country_code) || this.country_code.value === 'US'); // all eu countries and US have the option to choose between company and individual
                        break;
                    }
                }
            }

            if (passValue === repeatPasswordValue && !this.signUpForm.get('repeatPassword').pristine) {
                // valid state
                this.signUpForm.get('repeatPassword').setErrors(null);
                this.signUpForm.get('repeatPassword').setValidators([Validators.required]);
            }

            if (this.formSubmitted && !this.signUpForm.get('password').pristine && this.helperService.isStrongPassword(passValue).is_strong) {
                // valid state
                this.signUpForm.get('password').setErrors(null);
                this.signUpForm.get('password').setValidators([Validators.required, Validators.minLength(12), Validators.maxLength(255)]);
            }
        });
    }

    setupSignUpForm() {
        this.signUpForm = this.formBuilder.group({
            password: [null, [Validators.required, Validators.minLength(12), Validators.maxLength(255)]],
            repeatPassword: [null, Validators.required],
            email: [null, [Validators.required, Validators.maxLength(255)]],
            country_code: [null, Validators.required],
            businessType: [this.isDemo ? BusinessType.COMPANY : null, Validators.required] // default to company on demo, we also hide it.
        });
    }

    async signUp(): Promise<boolean> {
        let created = false;
        this.formSubmitted = true;
        this.authPageService.loading.set(true);
        this.createAccountBtnLabel = this.translate.instant('sign_up.wait');

        if (!(euCountries.includes(this.country_code.value) || this.country_code.value === 'US')) {
            // only eu countries and US can be individual
            this.businessType.patchValue(BusinessType.COMPANY);
        }

        // Validating password
        if (!this.helperService.isStrongPassword(this.password.value).is_strong) {
            this.signUpForm.get('password').setErrors({ weakPassword: true });
        } else if (this.password.value !== this.repeatPassword.value) {
            this.signUpForm.get('repeatPassword').setErrors({ pwNoMatch: true });
        }

        if (this.signUpForm.valid && this.gdprChecked && this.termsChecked) {
            const createCustomerParams: CreateNewCustomer = {
                email: this.email.value.replace(/\s/g, '').toLowerCase(),
                password: this.password.value,
                businessType: this.businessType.value,
                country: this.country_code.value,
                tos: {
                    accepted_terms_and_conditions: true,
                    accepted_gdpr: true
                }
            };
            await this.authService.createUser(createCustomerParams);

            // wait 10 seconds if demo to allow stripe to verify, better than waiting 10 seconds on backend.
            if (this.isDemo) {
                this.authPageService.displayAlmostDone.set(true);
                setTimeout(() => {
                    this.authPageService.displayAlmostDone.set(false);
                }, 12500 * 2);
            }
            this.toastService.success(this.translate.instant('account.check_inbox'), this.translate.instant('account.account_successfully_created'));
            created = true;
            this.authPageService.loading.set(false);
            return created;
        } else {
            this.createAccountBtnLabel = this.translate.instant('sign_up.create_account');
            this.authPageService.loading.set(false);
            return created;
        }
    }

    toggleConsent(typeOfConsent: string) {
        if (typeOfConsent == 'terms' && !this.termsChecked) {
            this.termsChecked = true;
        } else if (typeOfConsent == 'terms' && this.termsChecked) {
            this.termsChecked = false;
        }

        if (typeOfConsent == 'gdpr' && !this.gdprChecked) {
            this.gdprChecked = true;
        } else if (typeOfConsent == 'gdpr' && this.gdprChecked) {
            this.gdprChecked = false;
        }
    }

    get password() {
        return this.signUpForm.get('password');
    }
    get repeatPassword() {
        return this.signUpForm.get('repeatPassword');
    }
    get email() {
        return this.signUpForm.get('email');
    }
    get country_code() {
        return this.signUpForm.get('country_code');
    }
    get businessType() {
        return this.signUpForm.get('businessType');
    }
}
